import {
  addDayKey,
  dayKeyInZone,
  instantFromWall,
  weekdayOfDayKey,
  zonedParts,
  type DayKey,
} from '@/features/calendar/zone';
import { dateLocale, ownerTimeZone } from '@/utils/format';

/**
 * Send later and Snooze (ADR 0076): WHEN, as pure functions.
 *
 * Every choice is a wall-clock time in the owner's zone — Settings → General
 * when set, else this device's (`ownerTimeZone`, the same zone ADR 0075's
 * "Remind me" presets count days in) — turned into one instant with the
 * calendar's DST-correct `instantFromWall`. "Tomorrow at 8:00" is therefore
 * 8:00 on the wall clock there even across a spring-forward or fall-back
 * night, and the instant is what goes on the wire, in UTC.
 *
 * Everything takes `now` and the zone as arguments, so a test can stand in
 * New York on the night the clocks change on a machine set to UTC.
 */

/** Mornings are 08:00 (Gmail's choice), afternoons 13:00. */
const MORNING = 8 * 60;
const AFTERNOON = 13 * 60;
/** "Later today" is at least this far ahead, rounded up to the hour… */
const LATER_HOURS = 3;
/** …and is not offered once it is past this time of day. */
const LAST_LATER_MINUTES = 18 * 60;
/** The server's ceiling for how far ahead (ADR 0076 §7). */
export const MAX_DAYS_AHEAD = 366;

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

export type SchedulePresetId = 'tomorrow-morning' | 'tomorrow-afternoon' | 'monday-morning';
export type SnoozePresetId = 'later-today' | 'tomorrow' | 'this-weekend' | 'next-week';

/** One one-tap choice: a name, the instant, and the instant in words. */
export interface WhenPreset<Id extends string = string> {
  id: Id;
  label: string;
  at: Date;
  /** "Tue, Sep 24, 8:00 AM", in the owner's zone. */
  when: string;
}

/** The next Monday strictly after `today` (on a Monday, the one a week on). */
function nextMonday(today: DayKey): DayKey {
  return addDayKey(today, (8 - weekdayOfDayKey(today)) % 7 || 7);
}

function preset<Id extends string>(id: Id, label: string, at: Date, zone: string, now: Date): WhenPreset<Id> {
  return { id, label, at, when: formatWhen(at, zone, now) };
}

/** The composer's Schedule send menu: tomorrow morning and afternoon, Monday morning. */
export function schedulePresets(now: Date = new Date(), zone: string = ownerTimeZone()): WhenPreset<SchedulePresetId>[] {
  const today = dayKeyInZone(now, zone);
  const tomorrow = addDayKey(today, 1);
  return [
    preset('tomorrow-morning', 'Tomorrow morning', instantFromWall(tomorrow, MORNING, zone), zone, now),
    preset('tomorrow-afternoon', 'Tomorrow afternoon', instantFromWall(tomorrow, AFTERNOON, zone), zone, now),
    preset('monday-morning', 'Monday morning', instantFromWall(nextMonday(today), MORNING, zone), zone, now),
  ];
}

/**
 * Snooze's presets (ADR 0076 §16): later today (three hours on, rounded UP to
 * the next whole hour; not offered after 18:00), tomorrow 08:00, this weekend
 * (Saturday 08:00; not offered on a Saturday or Sunday) and next week (the
 * next Monday, 08:00).
 */
export function snoozePresets(now: Date = new Date(), zone: string = ownerTimeZone()): WhenPreset<SnoozePresetId>[] {
  const today = dayKeyInZone(now, zone);
  const wall = zonedParts(now, zone);
  const out: WhenPreset<SnoozePresetId>[] = [];

  if (wall.hour * 60 + wall.minute + (wall.second > 0 ? 1 : 0) <= LAST_LATER_MINUTES) {
    out.push(preset('later-today', 'Later today', laterToday(now, zone), zone, now));
  }
  out.push(preset('tomorrow', 'Tomorrow', instantFromWall(addDayKey(today, 1), MORNING, zone), zone, now));
  const weekday = weekdayOfDayKey(today);
  if (weekday !== 0 && weekday !== 6) {
    const saturday = addDayKey(today, 6 - weekday);
    out.push(preset('this-weekend', 'This weekend', instantFromWall(saturday, MORNING, zone), zone, now));
  }
  out.push(preset('next-week', 'Next week', instantFromWall(nextMonday(today), MORNING, zone), zone, now));
  return out;
}

/**
 * Now plus three REAL hours, then up to the next whole hour on the zone's
 * wall clock. Counted in real time, so across a spring-forward night 00:30
 * + 3 h is 04:30 on the clock and the snooze wakes at 05:00, three and a
 * half hours on — never earlier than three.
 */
export function laterToday(now: Date, zone: string = ownerTimeZone()): Date {
  const ahead = new Date(now.getTime() + LATER_HOURS * HOUR_MS);
  const parts = zonedParts(ahead, zone);
  const intoHour = parts.minute * 60_000 + parts.second * 1000 + ahead.getUTCMilliseconds();
  return intoHour === 0 ? ahead : new Date(ahead.getTime() + HOUR_MS - intoHour);
}

/** "Tue, Sep 24, 8:00 AM" in the owner's zone; the year too when it is not this one. */
export function formatWhen(at: Date, zone: string = ownerTimeZone(), now: Date = new Date()): string {
  const sameYear = zonedParts(at, zone).year === zonedParts(now, zone).year;
  const options: Intl.DateTimeFormatOptions = {
    timeZone: zone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  };
  try {
    return new Intl.DateTimeFormat(dateLocale(), options).format(at);
  } catch {
    return new Intl.DateTimeFormat(undefined, { ...options, timeZone: undefined }).format(at);
  }
}

/** The instant as the wire carries it: RFC 3339 in UTC, whole seconds. */
export function wireInstant(at: Date): string {
  return at.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/** A picked date and time, as the `<input type="date">` / `"time"` values. */
export interface PickedWhen {
  date: string;
  time: string;
}

/** Where "Pick date & time" starts: tomorrow, 08:00. */
export function defaultPick(now: Date = new Date(), zone: string = ownerTimeZone()): PickedWhen {
  const tomorrow = addDayKey(dayKeyInZone(now, zone), 1);
  return { date: tomorrow, time: '08:00' };
}

/** Today in the zone, for the date input's `min`. */
export function todayIn(now: Date = new Date(), zone: string = ownerTimeZone()): DayKey {
  return dayKeyInZone(now, zone);
}

/** The last day the server accepts, for the date input's `max`. */
export function lastDayIn(now: Date = new Date(), zone: string = ownerTimeZone()): DayKey {
  return dayKeyInZone(new Date(now.getTime() + MAX_DAYS_AHEAD * DAY_MS), zone);
}

/**
 * A picked date and time as an instant, or why it cannot be one: it must be a
 * real date and time, in the future, and at most 366 days ahead.
 */
export function pickedInstant(
  picked: PickedWhen,
  now: Date = new Date(),
  zone: string = ownerTimeZone(),
): { at: Date } | { error: string } {
  const date = /^(\d{4})-(\d{2})-(\d{2})$/.exec(picked.date);
  const time = /^(\d{2}):(\d{2})$/.exec(picked.time);
  if (!date) return { error: 'Choose a date.' };
  if (!time) return { error: 'Choose a time.' };
  const hours = Number(time[1]);
  const minutes = Number(time[2]);
  if (hours > 23 || minutes > 59) return { error: 'Choose a time.' };
  // 2026-02-31 is not a day: `Date.UTC` would quietly make it March 3rd.
  if (addDayKey(picked.date, 0) !== picked.date) return { error: 'Choose a date.' };
  const at = instantFromWall(picked.date, hours * 60 + minutes, zone);
  if (at.getTime() <= now.getTime()) return { error: 'Choose a time in the future.' };
  if (at.getTime() > now.getTime() + MAX_DAYS_AHEAD * DAY_MS) {
    return { error: `Choose a time within the next ${MAX_DAYS_AHEAD} days.` };
  }
  return { at };
}
